import { UploadOutlined } from "@ant-design/icons";
import { Button, Drawer, Input, message, Modal, Select, Space, Upload } from "antd";
import dayjs from "dayjs";
import { uploadFile } from "../../../../apis/misc.api";
import { useEffect, useRef, useState } from "react";
import { fetchBranchMembers } from "../../../../apis/branch.api";
import {
  editPI,
  getEstimateTransactions,
} from "../../../../apis/pro_forma_invoice.api";
import { usePermissions } from "../../../../custom_hooks/permission/permission_hook";
import OrderLostReasonDrawer from "../../order_lost_drawer";
import { OrderPlacedDetailsDrawer } from "../../order_placed_details_drawer";
import { HOLD_STOCK_ALLOWED_FROM } from "./holdStockAllowedFrom";

export interface BankDetails {
  bank_account_number: string;
  bank_ifsc: string;
  branch_upi: string;
}
export interface PFIOrderDetailsProps {
  created_by: string;
  estimate_data: any;
  client: string;
  lead_id: string;
  quotation_no: string;
  stage: string;
  getPIData: Function;
  status: string;
  bank_details?: BankDetails;
  setDeliveryAddressDrawerVisible: Function;
  assigned_to?: string;
  amountToBePaid?: number;
  onWalletTopUp?: () => void;
  onPaymentMade?: () => void;
  hasActivePOs?: boolean;
}
const order_statuses = [
  "order_placed",
  "order_confirmed",
  "partly_shipped",
  "shipped",
  "partly_delivered",
  "delivered",
  "refunded",
];

const PFIOrderDetails = ({
  created_by,
  estimate_data,
  client,
  lead_id,
  getPIData,
  quotation_no,
  stage,
  status,
  bank_details,
  setDeliveryAddressDrawerVisible,
  assigned_to,
  amountToBePaid,
  onWalletTopUp,
  onPaymentMade,
  hasActivePOs,
}: PFIOrderDetailsProps) => {
  const { hasPermission } = usePermissions();
  const [show_order_placed_details_drawer, setShowOrderPlacedDetailsDrawer] =
    useState(false);
  const [updateAsAdmin, setUpdateAsAdmin] = useState(false);
  const [orgUsers, setOrgUsers] = useState<any[]>([]);
  const [assignedTo, setAssignedTo] = useState<any>(
    estimate_data?.assigned_to?.id
  );
  const [isAddCashMode, setIsAddCashMode] = useState(false);
  const [customerPoModalOpen, setCustomerPoModalOpen] = useState(false);
  const [customerPo, setCustomerPo] = useState<string>(
    estimate_data?.customer_po ?? ""
  );
  const [customerPoFile, setCustomerPoFile] = useState<File | null>(null);
  const [savingCustomerPo, setSavingCustomerPo] = useState(false);

  const [order_lost_drawer_visible, setOrderLostDrawerVisible] =
    useState(false);
  const [isHalfPaid, setIsHalfPaid] = useState(false);
  const prevIsHalfPaid = useRef(false);
  const paymentMadeInDrawer = useRef(false);


  const isFullyPaid = amountToBePaid !== undefined && amountToBePaid === 0;
  const canHoldStock =
    hasPermission("pi.mark_as_order_placed") &&
    (isHalfPaid || isFullyPaid) &&
    HOLD_STOCK_ALLOWED_FROM.has(estimate_data?.estimate_status);
  const statusOptions = [
    { id: "1", value: "draft", label: "Draft", disabled: true },
    {
      id: "13",
      value: "incomplete",
      label: "Incomplete",
      disabled: !hasPermission("refund.approve"),
    },
    {
      id: "2",
      value: "availability_check",
      label: "Request Delivery Timeline",
      // disabled: true,
      disabled:
        !hasPermission("refund.approve") ||
        (estimate_data?.estimate_status === "payment_pending" && hasActivePOs),
    },
    {
      id: "14",
      value: "payment_pending",
      label: "Hold Stock",
      disabled: !canHoldStock,
    },
    {
      id: "3",
      value: "quote_approval_pending",
      label: "Quote Approval pending",
      disabled: estimate_data?.estimate_status === "payment_pending",
    },
    {
      id: "4",
      value: "order_placed",
      label: "Order Placed",
      disabled: hasPermission("pi.mark_as_order_placed") ? false : true,
    },

    // {
    //   id: "12",
    //   value: "order_cancelled",
    //   label: "Order Cancelled",
    //   disabled: true,
    // },
    {
      id: "6",
      value: "order_confirmed",
      label: "Order confirmed",
      disabled: true,
    },
    {
      id: "7",
      value: "partly_shipped",
      label: "Partly shipped",
      disabled: true,
    },
    {
      id: "8",
      value: "shipped",
      label: "Shipped",
      disabled: true,
    },
    {
      id: "9",
      value: "partly_delivered",
      label: "Partly delivered",
      disabled: true,
    },
    {
      id: "10",
      value: "delivered",
      label: "Delivered",
      disabled: !hasPermission("refund.approve"),
    },
    // "Refunded" was removed here: OMS owns refunds now. It ran a legacy multi-step flow
    // (cancel-and-wallet-refund → refund-estimate-items → refund-service-invoice → set status)
    // that records nothing in the OMS refund ledger, so per-payment headroom could not see it and
    // the same payment could be refunded twice. Refund from the OMS sales-order screen instead;
    // those four endpoints now refuse OMS-managed estimates server-side regardless of this UI.
  ];

  useEffect(() => {
    setAssignedTo(estimate_data?.assigned_to?.id);
  }, [estimate_data?.assigned_to]);

  useEffect(() => {
    setCustomerPo(estimate_data?.customer_po ?? "");
  }, [estimate_data?.customer_po]);

  const openCustomerPoModal = () => {
    setCustomerPo(estimate_data?.customer_po ?? "");
    setCustomerPoFile(null);
    setCustomerPoModalOpen(true);
  };

  const handleSaveCustomerPo = async () => {
    const trimmed = customerPo.trim();
    setSavingCustomerPo(true);
    message.loading({ content: "Updating...", key: "customer_po" });
    try {
      const payload: Record<string, any> = { customer_po: trimmed || null };
      if (customerPoFile) {
        const poFormData = new FormData();
        poFormData.append("file", customerPoFile, customerPoFile.name);
        poFormData.append("file_name", customerPoFile.name);
        if (estimate_data?.client) {
          poFormData.append("client", estimate_data?.client);
        }
        const po_url = await uploadFile(poFormData);
        if (!po_url) {
          message.error({
            content: "Failed to upload Customer PO file. Please retry.",
            key: "customer_po",
            duration: 2,
          });
          setSavingCustomerPo(false);
          return;
        }
        payload.customer_po_link = po_url;
      }
      await editPI(estimate_data?.id, payload);
      message.success({
        content: "Customer PO updated",
        key: "customer_po",
        duration: 2,
      });
      setCustomerPoModalOpen(false);
      setCustomerPoFile(null);
      getPIData();
    } catch (e) {
      message.error({
        content: "Unable to update Customer PO",
        key: "customer_po",
        duration: 2,
      });
    } finally {
      setSavingCustomerPo(false);
    }
  };

  useEffect(() => {
    const id = estimate_data?.id;
    const total = Number(estimate_data?.total_estimate_amount ?? 0);
    if (!id || total <= 0) {
      setIsHalfPaid(false);
      return;
    }
    if (!HOLD_STOCK_ALLOWED_FROM.has(estimate_data?.estimate_status)) {
      setIsHalfPaid(false);
      return;
    }
    // If wallet-adjusted pending is available use it — it already accounts for
    // wallet credits so half-paid is true whenever the remaining amount is ≤ 90%.
    if (amountToBePaid !== undefined) {
      setIsHalfPaid(amountToBePaid <= total * 90 / 100 && amountToBePaid > 0);
      return;
    }
    getEstimateTransactions(id)
      .then((txns: any[]) => {
        const paid =
          (txns ?? [])
            .filter((t: any) =>
              ["captured", "authorized", "authenticated"].includes(t?.status)
            )
            .reduce(
              (acc: number, t: any) => acc + Number(t.payment_amount ?? 0),
              0
            ) / 100;
        setIsHalfPaid(paid >= total * 10 / 100);
      })
      .catch(() => setIsHalfPaid(false));
  }, [estimate_data?.id, estimate_data?.estimate_status, estimate_data?.total_estimate_amount, amountToBePaid]);

  useEffect(() => {
    if (
      isHalfPaid &&
      !prevIsHalfPaid.current &&
      paymentMadeInDrawer.current &&
      HOLD_STOCK_ALLOWED_FROM.has(estimate_data?.estimate_status)
    ) {
      paymentMadeInDrawer.current = false;
      const total = Number(estimate_data?.total_estimate_amount ?? 0);
      const paid = total - Number(amountToBePaid ?? 0);
      const formattedStatus = estimate_data?.estimate_status?.split("_").map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ");
      Modal.confirm({
        title: "10% Payment Received",
        content: `₹${paid.toFixed(2)} covered of ₹${total.toFixed(
          2
        )}. Switch to Hold Stock, or continue at ${formattedStatus}?`,
        okText: "Hold Stock",
        cancelText: "Continue",
        onOk: async () => {
          try {
            await editPI(estimate_data?.id, {
              estimate_status: "payment_pending",
            });
            message.success("Hold Stock marked");
            getPIData();
          } catch {
            message.error("Failed to mark Hold Stock");
          }
        },
      });
    }
    prevIsHalfPaid.current = isHalfPaid;
  }, [isHalfPaid, estimate_data?.estimate_status, estimate_data?.total_estimate_amount, amountToBePaid]);

  useEffect(() => {
    fetchBranchMembers()
      .then((res) => {
        setOrgUsers(res);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [estimate_data]);

  const onOrderLostReasonSubmit = (values: any) => {
    setOrderLostDrawerVisible(false);
    editPI(estimate_data?.id, {
      ...values,
      estimate_status: "order_lost",
    }).then((res) => {
      getPIData();
    });
  };

  const onOrderPlacedDetailsFinish = (values: any) => {
    setShowOrderPlacedDetailsDrawer(false);
    if (isAddCashMode) {
      onWalletTopUp?.();
      setIsAddCashMode(false);
    } else {
      paymentMadeInDrawer.current = true;
      onPaymentMade?.();
    }
    // editPI(
    //   estimate_data?.id,
    //   {
    //     // ...values,
    //     // order_placed_time: moment(new Date()).add(5.5, "hours").toISOString(),
    //     estimate_status: "order_placed",
    //   },
    //   updateAsAdmin
    // ).then((res) => {
    //   if (updateAsAdmin) {
    //     message.success("PI updated successfully");
    //   } else {
    //     message.info(
    //       "Whatsapp message sent to client. PI will be marked as order placed once client confirms."
    //     );
    //   }
    // });
    getPIData();
    setUpdateAsAdmin(false);
  };

  return (
    <div className="p-3 py-2 row mb-2">
      <div className="th-font-size-16 th-font-weight-600 pb-3 primary-color-text">
        Order Details
      </div>
      <div className="col">
        <div className="row">
          <div className="col-4 th-font-weight-500">Created By</div>
          <div className="col-8 pb-1">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div>{created_by}</div>
            </div>
          </div>
        </div>
        <div className="row py-2">
          <div className="col-4 th-font-weight-500">Assigned To</div>
          <div className="col-8 pb-1">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div>
                {hasPermission("pi.change_assigned_to") ? (
                  <Select
                    value={assignedTo}
                    onChange={(id) => {
                      message.loading({
                        content: "Updating...",
                        key: "updatable",
                      });
                      editPI(estimate_data?.id, {
                        assigned_to: id,
                      })
                        .then((res) => {
                          getPIData();
                          message.success({
                            content: "Updated successfully",
                            key: "updatable",
                            duration: 2,
                          });
                        })
                        .catch((e) => {
                          message.error({
                            content: "Unable to update",
                            key: "updatable",
                            duration: 2,
                          });

                          setAssignedTo(estimate_data?.assigned_to?.id);
                        });
                    }}
                    options={orgUsers.map((orgUser) => ({
                      value: orgUser?.user?.id,
                      label: orgUser?.user?.f_name,
                    }))}
                    placeholder="Select user"
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    style={{ width: 210 }}
                  ></Select>
                ) : (
                  assigned_to
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="row py-2">
          <div className="col-4 th-font-weight-500">Created At</div>
          <div className="col-8">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div>
                {dayjs(estimate_data?.created_at).format("DD-MM-YYYY HH:mm")}
              </div>
            </div>
          </div>
        </div>
        <div className="row py-2">
          <div className="col-4 th-font-weight-500">Client </div>
          <div className="col-8">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div>{client}</div>
            </div>
          </div>
        </div>
        <div className="row py-2">
          <div className="col-4 th-font-weight-500">Lead ID</div>
          <div className="col-8">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div>{lead_id}</div>
            </div>
          </div>
        </div>
        <div className="row py-2">
          <div className="col-4 th-font-weight-500">Customer PO No.</div>
          <div className="col-8">
            <div className="d-flex align-items-center">
              <div className="pe-1">:</div>
              <div>{estimate_data?.customer_po || "—"}</div>
              {hasPermission("pi.edit_customer_po") && (
                <Button
                  size="small"
                  type="link"
                  className="ms-2 p-0"
                  style={{ height: "auto", lineHeight: 1 }}
                  onClick={openCustomerPoModal}
                >
                  Edit
                </Button>
              )}
            </div>
          </div>
        </div>
        {estimate_data?.customer_po_link && (
          <div className="row py-2">
            <div className="col-4 th-font-weight-500">Customer PO File</div>
            <div className="col-8">
              <div className="d-flex">
                <div className="pe-1">:</div>
                <div>
                  <a
                    href={estimate_data?.customer_po_link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View file
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* <div className="row py-2">
          <div className="col-4 th-font-weight-500">Quotation No.</div>
          <div className="col-8">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div>{quotation_no}</div>
            </div>
          </div>
        </div> */}
        <div className="row py-2">
          <div className="col-4 th-font-weight-500">Quotation Status</div>
          <div className="col-8">
            <div className="d-flex flex-column">
              <div className="d-flex pb-2">
                <div className="pe-1">:</div>
                <Space>
                  <Select
                  style={{ width: 210 }}
                  onChange={async (e) => {
                    if (
                      (estimate_data?.ship_address != null &&
                        e == "order_placed") ||
                      (e != "order_placed" && e != "order_lost")
                    ) {
                      if (e === "order_placed") {
                        setIsAddCashMode(false);
                        setShowOrderPlacedDetailsDrawer(true);
                        return;
                      }

                      const payload = {
                        estimate_status: e,
                      } as Record<string, any>;
                      if (e === "order_placed") {
                        payload.order_placed_time = new Date().toISOString();
                      }
                      await editPI(estimate_data?.id, payload).then(
                        (res) => {}
                      );
                    } else if (e == "order_lost") {
                      setOrderLostDrawerVisible(true);
                    } else {
                      setDeliveryAddressDrawerVisible(true);
                    }

                    getPIData();
                  }}
                  disabled={
                    hasPermission("order.update")
                      ? false
                      : order_statuses.includes(status)
                  }
                  placeholder="Select status"
                  value={status}
                  options={statusOptions.map((item: any) => ({
                    value: item?.value,
                    label: item?.label,
                    disabled: item?.disabled ?? false,
                  }))}
                ></Select>
                {hasPermission("pi.mark_as_order_placed") && (
                  <Button
                    size="small"
                    type="primary"
                    onClick={async () => {
                      setUpdateAsAdmin(true);
                      setIsAddCashMode(false);
                      setShowOrderPlacedDetailsDrawer(true);
                    }}
                  >
                    Mark as Placed (admin)
                  </Button>
                )}
              </Space>
            </div>
            <div className="ps-2">
              <Button 
                size="small" 
                onClick={() => {
                  setIsAddCashMode(true);
                  setShowOrderPlacedDetailsDrawer(true);
                }}
              >
                Add Cash for Customer
              </Button>
            </div>
          </div>
        </div>
        </div>

        {/* <div className="row py-2">
          <div className="col-4 th-font-weight-500">Payment Confirmation</div>
          <div className="col-8">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div>
                https://materialdepot.in/external-payment/external-success?id=
                {estimate_data?.id}&lead_id={lead_id}
                <br />
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      `https://materialdepot.in/external-payment/external-success?id=${estimate_data?.id}&lead_id=${lead_id}`
                    );
                    message.success("Link copied to clipboard");
                  }}
                >
                  Copy Link
                </Button>
              </div>
            </div>
          </div>
        </div> */}
        {/* <div className="row py-2">
          <div className="col-4 th-font-weight-500">Bank Details</div>
          <div className="col-8">
            <div className="d-flex">
              <div className="pe-1">:</div>
              <div className="">
                <div>Account Number ({bank_details?.bank_account_number})</div>
                <div>IFSC Code ({bank_details?.bank_ifsc})</div>
                <div>UPI ({bank_details?.branch_upi})</div>
              </div>
            </div>
          </div>
        </div> */}
      </div>

      <Modal
        title="Edit Customer PO"
        open={customerPoModalOpen}
        okText="Save"
        confirmLoading={savingCustomerPo}
        onOk={handleSaveCustomerPo}
        onCancel={() => {
          if (savingCustomerPo) return;
          setCustomerPoModalOpen(false);
          setCustomerPoFile(null);
          setCustomerPo(estimate_data?.customer_po ?? "");
        }}
        destroyOnClose
      >
        <div className="mb-3">
          <div className="th-font-weight-500 mb-1">Customer PO No.</div>
          <Input
            value={customerPo}
            placeholder="Enter Customer PO Number"
            onChange={(e) => setCustomerPo(e.target.value)}
            disabled={savingCustomerPo}
          />
        </div>
        <div>
          <div className="th-font-weight-500 mb-1">Customer PO File</div>
          <div className="d-flex align-items-center gap-2">
            <Upload
              showUploadList={false}
              accept=".pdf"
              beforeUpload={(file) => {
                if (file.type !== "application/pdf") {
                  message.error("Only PDF files are allowed.");
                  return false;
                }
                if (file.size > 5 * 1024 * 1024) {
                  message.error("File too large. Maximum size is 5MB.");
                  return false;
                }
                setCustomerPoFile(file);
                return false;
              }}
            >
              <Button icon={<UploadOutlined />} disabled={savingCustomerPo}>
                Upload
              </Button>
            </Upload>
            {customerPoFile ? (
              <span>{customerPoFile.name} (will upload on save)</span>
            ) : (
              estimate_data?.customer_po_link && (
                <a
                  href={estimate_data?.customer_po_link}
                  target="_blank"
                  rel="noreferrer"
                >
                  View file
                </a>
              )
            )}
          </div>
        </div>
      </Modal>

      <OrderLostReasonDrawer
        visible={order_lost_drawer_visible}
        onFinish={onOrderLostReasonSubmit}
        onClose={() => {
          setOrderLostDrawerVisible(false);
        }}
      />

      <Drawer
        title="Order Placed Details"
        placement="right"
        closable={false}
        onClose={() => {}}
        open={show_order_placed_details_drawer}
      >
        <OrderPlacedDetailsDrawer
          onFinish={onOrderPlacedDetailsFinish}
          onCancel={() => {
            setShowOrderPlacedDetailsDrawer(false);
          }}
          estimateId={estimate_data?.id}
          isAddCashToWallet={isAddCashMode}
          customPendingAmount={amountToBePaid}
        />
      </Drawer>
    </div>
  );
};

export default PFIOrderDetails;
